import { useCallback, useMemo, type UIEvent } from "react";
import { useInfiniteQuery } from "@tanstack/react-query";
import {
  brandService,
  BRANDS_PAGE_SIZE,
  type BrandOption,
  type BrandSource,
} from "@/services/BrandService";
import { useDebounce } from "@/hooks/useDebounce";

export const getInfiniteBrandsQueryKey = (
  search: string,
  source: BrandSource = "brands",
) => ["brands", "infinite", search, source] as const;

interface UseInfiniteBrandsParams {
  search: string;
  enabled?: boolean;
  source?: BrandSource;
}

export const useInfiniteBrands = ({
  search,
  enabled = true,
  source = "brands",
}: UseInfiniteBrandsParams) => {
  const debouncedSearch = useDebounce(search, 300);

  const query = useInfiniteQuery({
    queryKey: getInfiniteBrandsQueryKey(debouncedSearch, source),
    queryFn: ({ pageParam }) =>
      brandService.getBrandsPage({
        search: debouncedSearch,
        page: pageParam,
        source,
      }),
    initialPageParam: 0,
    getNextPageParam: (lastPage, _allPages, lastPageParam) =>
      lastPage.length === BRANDS_PAGE_SIZE ? lastPageParam + 1 : undefined,
    enabled,
    staleTime: 1000 * 60 * 5,
  });

  const brands = useMemo<BrandOption[]>(
    () => query.data?.pages.flatMap((page) => page) ?? [],
    [query.data],
  );

  const handleScrollEnd = useCallback(
    (event: UIEvent<HTMLElement>) => {
      const element = event.currentTarget;
      const distanceFromBottom =
        element.scrollHeight - element.scrollTop - element.clientHeight;

      if (distanceFromBottom > 50) return;
      if (!query.hasNextPage || query.isFetchingNextPage) return;

      void query.fetchNextPage();
    },
    [query.fetchNextPage, query.hasNextPage, query.isFetchingNextPage],
  );

  return {
    brands,
    debouncedSearch,
    handleScrollEnd,
    isPending: query.isPending,
    isFetchingNextPage: query.isFetchingNextPage,
    hasNextPage: query.hasNextPage ?? false,
  };
};
